import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePassword } from 'utils'
import { showAlert } from 'services/alerts.service'
import { TIMEZONES, COUNTRIES, CURRENCIES, MONTHS } from 'constants/farmSettings'
import ModalComponent from 'components/common/ModalComponent'
import LabeledInputComponent from 'components/common/LabeledInputComponent'
import LabeledSelectComponent from 'components/common/LabeledSelectComponent'
import FormFooter from 'components/System/FormFooter'
import PasswordField from 'components/System/PasswordField'
import { systemRequest, getRequestErrorMessage, getMissingFields } from 'components/System/systemRequest'

const REQUIRED_FIELDS = [
  'firstName', 'lastName', 'email', 'farmName', 'timezone', 'currency',
  'costBaseHour', 'seasonStartDay', 'seasonStartMonth'
]

const toFormData = tenant => tenant
  ? {
      firstName: tenant.adminFirstName || '',
      lastName: tenant.adminLastName || '',
      email: tenant.adminEmail || '',
      farmName: tenant.farmName || '',
      country: tenant.country || '',
      timezone: tenant.timezone || '',
      currency: tenant.currency || '',
      costBaseHour: tenant.costBaseHour ?? '',
      seasonStartDay: tenant.seasonStartDay ?? '',
      seasonStartMonth: tenant.seasonStartMonth || ''
    }
  : {
      firstName: '',
      lastName: '',
      email: '',
      password: generatePassword(12),
      farmName: '',
      country: 'Mexico',
      timezone: 'America/Mexico_City',
      currency: 'MXN',
      costBaseHour: '',
      seasonStartDay: 1,
      seasonStartMonth: '1'
    }

// Create (farm + owner admin + password) or edit a tenant
const TenantFormModal = ({ isOpen, tenant, onClose, onSaved }) => {
  const { t } = useTranslation()
  const [missingFields, setMissingFields] = useState([])
  const [saving, setSaving] = useState(false)
  const isEditing = !!tenant
  const initialData = useMemo(() => toFormData(tenant), [tenant, isOpen])

  useEffect(() => {
    if (isOpen) setMissingFields([])
  }, [isOpen])

  const handleSave = async formData => {
    const missing = getMissingFields(formData, isEditing ? REQUIRED_FIELDS : [...REQUIRED_FIELDS, 'password'])
    setMissingFields(missing)
    if (missing.length) {
      showAlert({ message: t('system.tenants.form.missingFields'), icon: 'warning' })
      return
    }

    const payload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      farmName: formData.farmName.trim(),
      country: formData.country || null,
      timezone: formData.timezone,
      currency: formData.currency,
      costBaseHour: Number(formData.costBaseHour),
      seasonStartDay: Number(formData.seasonStartDay),
      seasonStartMonth: String(formData.seasonStartMonth),
      ...(!isEditing && { password: formData.password })
    }

    setSaving(true)
    try {
      await systemRequest(
        isEditing ? 'PUT' : 'POST',
        isEditing ? `/system/tenants/${tenant.farmId}` : '/system/tenants',
        payload
      )
      showAlert({ message: t(isEditing ? 'system.tenants.form.updated' : 'system.tenants.form.created'), icon: 'success' })
      onSaved()
    } catch (error) {
      showAlert({ message: getRequestErrorMessage(error, t('system.tenants.form.saveError')), icon: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const missingSet = new Set(missingFields)

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('system.tenants.form.editTitle', { name: tenant.farmName }) : t('system.tenants.form.createTitle')}
      initialData={initialData}
      onSave={handleSave}
      maxWidth='3xl'
      customFooter={<FormFooter onClose={onClose} saving={saving} />}
    >
      {({ formData, handleChange }) => (
        <div className='flex flex-col gap-6'>
          <section>
            <h4 className='font-semibold text-greenPrimary mb-3'>{t('system.tenants.form.ownerSection')}</h4>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <LabeledInputComponent
                label={t('system.tenants.form.firstName')}
                name='firstName'
                type='text'
                value={formData.firstName || ''}
                onChange={handleChange}
                missingFields={missingSet}
                required
              />
              <LabeledInputComponent
                label={t('system.tenants.form.lastName')}
                name='lastName'
                type='text'
                value={formData.lastName || ''}
                onChange={handleChange}
                missingFields={missingSet}
                required
              />
              <LabeledInputComponent
                label={t('system.tenants.form.email')}
                name='email'
                type='email'
                value={formData.email || ''}
                onChange={handleChange}
                missingFields={missingSet}
                required
              />
              {!isEditing && (
                <PasswordField
                  value={formData.password}
                  onChange={handleChange}
                  isMissing={missingSet.has('password')}
                />
              )}
            </div>
          </section>

          <section>
            <h4 className='font-semibold text-greenPrimary mb-3'>{t('system.tenants.form.farmSection')}</h4>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <LabeledInputComponent
                label={t('mgmt.settings.farm.farmName')}
                name='farmName'
                type='text'
                value={formData.farmName || ''}
                onChange={handleChange}
                missingFields={missingSet}
                required
              />
              <LabeledSelectComponent
                name='country'
                label={t('mgmt.settings.farm.country')}
                value={formData.country || ''}
                onChange={handleChange}
              >
                {COUNTRIES.map(country => (
                  <option key={country.value} value={country.value}>{country.label}</option>
                ))}
              </LabeledSelectComponent>
              <LabeledSelectComponent
                name='timezone'
                label={t('mgmt.settings.farm.timezone')}
                value={formData.timezone || ''}
                onChange={handleChange}
                missingFields={missingFields}
                required
              >
                {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </LabeledSelectComponent>
              <LabeledSelectComponent
                name='currency'
                label={t('mgmt.settings.farm.currency')}
                value={formData.currency || ''}
                onChange={handleChange}
                missingFields={missingFields}
                required
              >
                {CURRENCIES.map(currency => <option key={currency} value={currency}>{currency}</option>)}
              </LabeledSelectComponent>
              <LabeledInputComponent
                label={t('mgmt.settings.farm.costBaseHour')}
                name='costBaseHour'
                type='number'
                min='0'
                step='1'
                value={String(formData.costBaseHour ?? '')}
                onChange={handleChange}
                missingFields={missingSet}
                required
              />
              <LabeledInputComponent
                label={t('mgmt.settings.farm.seasonStartDay')}
                name='seasonStartDay'
                type='number'
                min='1'
                max='31'
                value={String(formData.seasonStartDay ?? '')}
                onChange={handleChange}
                missingFields={missingSet}
                required
              />
              <LabeledSelectComponent
                name='seasonStartMonth'
                label={t('mgmt.settings.farm.seasonStartMonth')}
                value={String(formData.seasonStartMonth || '')}
                onChange={handleChange}
                missingFields={missingFields}
                required
              >
                {MONTHS.map(m => (
                  <option key={m} value={String(m)}>{t(`mgmt.settings.months.${m}`)}</option>
                ))}
              </LabeledSelectComponent>
            </div>
            {!isEditing && (
              <p className='mt-3 text-xs text-gray-500'>{t('system.tenants.form.defaultsHint')}</p>
            )}
          </section>
        </div>
      )}
    </ModalComponent>
  )
}

export default TenantFormModal
